import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, ShieldCheck, GitPullRequest, Users, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { STATUSES, STATUS_STYLES } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div data-testid="dashboard-loading" className="text-[#475569]">Loading dashboard...</div>;

  const cards = [
    { title: "Total Listings", value: stats.total_listings, icon: Store, accent: "#1B2A4A", testid: "stat-total-listings" },
    { title: "Pending Verifications", value: stats.pending_verifications, icon: ShieldCheck, accent: "#F5961B", testid: "stat-pending-verifications" },
    { title: "Category Requests", value: stats.pending_category_requests, icon: GitPullRequest, accent: "#059669", testid: "stat-pending-requests" },
    { title: "Active Employees", value: stats.active_employees, icon: Users, accent: "#2E7BD6", testid: "stat-active-employees" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">Dashboard</h1>
        <p className="text-sm text-[#475569] mt-1">Platform seeding overview</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <Card key={c.title} className="border-[#E2E8F0] shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.accent + "1A" }}>
                <c.icon size={20} style={{ color: c.accent }} />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{c.title}</div>
                <div className="text-2xl font-bold text-[#0F172A]" data-testid={c.testid}>{c.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#1B2A4A]">Listing Pipeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="pipeline-breakdown">
            {STATUSES.map((s) => {
              const count = stats.by_status[s] || 0;
              const pct = stats.total_listings ? Math.round((count / stats.total_listings) * 100) : 0;
              const st = STATUS_STYLES[s];
              return (
                <Link to={`/admin/listings?status=${s}`} key={s} data-testid={`pipeline-${s}`} className="block group">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-[#0F172A] group-hover:text-[#2E7BD6]">{st.label}</span>
                    <span className="text-[#475569]">{count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: st.text }} />
                  </div>
                </Link>
              );
            })}
            {stats.awaiting_category > 0 && (
              <Link to="/admin/category-requests" data-testid="pipeline-awaiting" className="flex items-center gap-2 text-sm text-[#F5961B] font-medium pt-1">
                {stats.awaiting_category} listing(s) awaiting category approval <ArrowRight size={14} />
              </Link>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-lg text-[#1B2A4A]">Recent Activity</CardTitle>
            <Link to="/admin/activity" data-testid="view-all-activity" className="text-sm text-[#2E7BD6] font-medium">View all</Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="recent-activity-list">
              {stats.recent_activity.length === 0 && <div className="text-sm text-[#94A3B8]">No activity yet.</div>}
              {stats.recent_activity.map((a) => (
                <div key={a.id} className="flex gap-3 text-sm">
                  <div className="w-1.5 rounded-full bg-[#2E7BD6]/30 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium text-[#0F172A]">{a.user_name}</span>{" "}
                    <span className="text-[#475569]">{a.action} {a.entity_type}</span>{" "}
                    <span className="font-medium text-[#1B2A4A]">{a.entity_name}</span>
                    <div className="text-xs text-[#94A3B8]">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
